import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import {
  toggleReviewSelection,
  openEditModal,
  BULK_GENERATE_MAX,
} from '../../store/slices/googleReviewSlice';
import type { GoogleReviewStatus, GoogleReviewView } from '../../types/googleReview';
import { dateFormatted } from '../../helpers/dateFormatted';

interface ReviewCardProps {
  review: GoogleReviewView;
  isSelected: boolean;
}

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <span className="review-card__stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? 'review-card__star--filled' : 'review-card__star--empty'}>
        ★
      </span>
    ))}
  </span>
);

const StatusBadge: React.FC<{ status?: GoogleReviewView['status'] }> = ({ status }) => {
  const { t } = useTranslation();
  if (!status) {
    return null;
  }

  const labels: Record<GoogleReviewStatus, string> = {
    needs_reply: t('google_review_status_needs_reply'),
    draft: t('google_review_status_draft'),
    published: t('google_review_status_published'),
  };
  return (
    <span className={`review-card__status review-card__status--${status}`}>
      {labels[status]}
    </span>
  );
};

const ReviewCard: React.FC<ReviewCardProps> = ({ review, isSelected }) => {
  // At the batch cap only already-selected rows stay tickable, so the count can go
  // down but never past BULK_GENERATE_MAX.
  const selectionFull = useAppSelector(
    (s) => s.googleReview.selectedReviewIds.length >= BULK_GENERATE_MAX,
  );
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Subuser: generating / editing / publishing replies requires google_review_write.
  const { can } = usePermissions();
  const canWrite = can('google_review_write');
  const reviewId = review.reviewId ?? '';

  const actionLabel = () => {
    if (review.status === 'needs_reply') return t('google_review_action_generate');
    if (review.status === 'draft') return t('google_review_action_edit_draft');
    return t('google_review_action_edit_published');
  };

  return (
    <div className={`review-card${isSelected ? ' review-card--selected' : ''}`}>

      {/* Row 1: checkbox + avatar + name/date + status badge */}
      <div className="review-card__header-row">
        <input
          type="checkbox"
          className="review-card__checkbox"
          checked={isSelected}
          disabled={selectionFull && !isSelected}
          title={
            selectionFull && !isSelected
              ? t('google_review_bulk_max_hint', { max: BULK_GENERATE_MAX })
              : undefined
          }
          onChange={() => reviewId && dispatch(toggleReviewSelection(reviewId))}
          aria-label={t('google_review_select_review', { name: review.reviewerName ?? '' })}
        />
        <div className="review-card__avatar" aria-hidden="true">
          {review.reviewerAvatarInitials}
        </div>
        <div className="review-card__meta">
          <span className="review-card__name">{review.reviewerName}</span>
          <span className="review-card__date">{dateFormatted(review.createdAt ?? '')}</span>
        </div>
        <StatusBadge status={review.status} />
      </div>

      {/* Content block: indented to align past checkbox + avatar */}
      <div className="review-card__content">
        <StarRating rating={review.rating ?? 0} />

        <p className="review-card__text">{review.text}</p>

        {review.status === 'published' && review.existingReply && (
          <div className="review-card__reply review-card__reply--published">
            <span className="review-card__reply-label">{t('google_review_reply_label_published')}</span>
            <p className="review-card__reply-text">{review.existingReply}</p>
          </div>
        )}

        {review.status === 'draft' && review.draftText && (
          <div className="review-card__reply review-card__reply--draft">
            <span className="review-card__reply-label">{t('google_review_draft_label')}</span>
            <p className="review-card__reply-text">{review.draftText}</p>
          </div>
        )}

        <button
          className={`review-card__action-btn${review.status === 'needs_reply' ? ' review-card__action-btn--primary' : ' review-card__action-btn--outline'}`}
          onClick={() => dispatch(openEditModal(review))}
          disabled={!canWrite}
          title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
        >
          {actionLabel()}
        </button>
      </div>

    </div>
  );
};

export default ReviewCard;
